/**
 * @file Renders a titled section with an edit button and a loading skeleton when it is empty.
 */
import React from "react";
import { Typography, Skeleton } from "antd";
import type { CSSProperties, ReactNode } from "react";

import { W1, PATH_BACKSTAGE } from "@components/Constants";

import EditButton, { type EditButtonProps } from "./EditButton";

const { Title, Text } = Typography;

type Props = {
  title: ReactNode;
  description?: string;
  level?: 1 | 2 | 3 | 4 | 5;
  style?: CSSProperties;
  onClick?: () => void | Promise<void>;
  children?: ReactNode;
  isEditable?: boolean;
  buttonProps?: EditButtonProps;
  marginBottom?: number;
};

/**
 * Renders the title, the description and the children of the section, with an edit
 * button in Backstage, and a loading skeleton in place of empty children.
 */
const EditableGroup = ({
  level = 5,
  style = {},
  onClick,
  children = null,
  isEditable = true,
  buttonProps = {},
  marginBottom = W1,
  title,
  description,
}: Props): JSX.Element | null => {
  const isBackstage = window.location.pathname.startsWith(PATH_BACKSTAGE);

  const shouldRenderNull = !children && !isBackstage;

  if (shouldRenderNull) {
    return null;
  }

  return (
    <div style={style}>
      <div style={{ marginBottom }}>
        {isEditable && <EditButton onClick={onClick} {...buttonProps} />}

        <Title level={level} style={{ marginTop: 0 }}>
          {title}
        </Title>

        {description && (
          <Text type="secondary">{description}</Text>
        )}
      </div>

      {children}

      {!children && <Skeleton />}
    </div>
  );
};

export default EditableGroup;
